import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import axios from "axios";

// Alerts about one conversation carry its pair id in meta (a JSON string).
// vault_line_contact (2026-10-08): contact details refused or spotted in the
// pair's typed Vault lines; the pair page shows them under Vault Unlock.
const PAIR_ALERT_TYPES = new Set(["offapp_contact_removed", "offapp_contact_possible", "split_number_attempt", "vault_line_contact"]);
const pairIdOf = (n) => {
  try {
    const meta = typeof n?.meta === "string" ? JSON.parse(n.meta) : n?.meta || {};
    const id = meta?.compatibilityId;
    return /^[a-f0-9]{24}$/.test(String(id || "")) ? String(id) : null;
  } catch {
    return null;
  }
};
// The live-connection alert carries the member's id in meta.memberId.
const memberIdOf = (n) => {
  try {
    const meta = typeof n?.meta === "string" ? JSON.parse(n.meta) : n?.meta || {};
    const id = meta?.memberId;
    return /^[a-f0-9]{24}$/.test(String(id || "")) ? String(id) : null;
  } catch {
    return null;
  }
};

const AdminNotificationDropdown = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const API_BASE = process.env.REACT_APP_BASE_URL;

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.get(`${API_BASE}/admin/notificationcountlist`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(res.data.data || []);
      setUnreadCount(res.data.totalCount || 0);
    } catch (err) {
      console.error("Error fetching notifications:", err);
    }
  };

  const markAsRead = async (ids) => {
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.post(
        `${API_BASE}/admin/updateReadCount`,
        { ids },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // ✅ If API confirms success, update local state
      if (res.data.modifiedCount > 0) {
        setNotifications((prev) =>
          prev.map((n) => (ids.includes(n._id) ? { ...n, isRead: "1" } : n))
        );

        // Re-read the real count rather than subtracting locally: the badge
        // counts ALL unread, while ids only covers what this dropdown was
        // shown, so local arithmetic drifts.
        fetchNotifications();
      }
    } catch (err) {
      console.error("Error marking notifications as read:", err);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 180000); // refresh every 3 min
    return () => clearInterval(interval);
  }, []);

  const handleClick = async (n) => {
    if (String(n.isRead) === "0") {
      // 🔴 THE WHOLE GROUP. A collapsed row stands for every notification of
      // its type, and sending only n._id left the rest unread, which is why
      // the badge never went down.
      await markAsRead(n.groupIds?.length ? n.groupIds : [n._id]);
    }
    // A live connection that claimed to be a member without proof (backend
    // utils/socketGuard, 2026-09-26) opens that member.
    if (n.notificationType === "socket_identity_unconfirmed") {
      const memberId = memberIdOf(n);
      navigate(memberId ? `/user/view-user/${memberId}` : "/user-report");
      return;
    }
    // Two accounts that may be one person (backend utils/dupeFlag,
    // 2026-10-13) open the Possible Duplicates list.
    if (n.notificationType === "duplicate_account_possible") {
      navigate("/possible-duplicates");
      return;
    }
    // A face check passed after a photo change, with photos unlike the
    // selfie (backend utils/faceCheckPhotoReview, 2026-10-13): opens that
    // member, whose photos are the thing to look at.
    if (n.notificationType === "face_check_photos_review") {
      const memberId = memberIdOf(n);
      navigate(memberId ? `/user/view-user/${memberId}` : "/user-report");
      return;
    }
    // A face check passed without a clear photo of the face where the member
    // could not change photos (backend utils/faceClear, 2026-10-14): opens
    // that member, whose photos are the thing to look at.
    if (n.notificationType === "face_check_unclear_review") {
      const memberId = memberIdOf(n);
      navigate(memberId ? `/user/view-user/${memberId}` : "/user-report");
      return;
    }
    // Chat safety alerts open the conversation itself (2026-09-24): the
    // off-app guard's removals and near-misses, and a phone number passed
    // across messages. Everything else still goes to the report queue.
    const pairId = PAIR_ALERT_TYPES.has(n.notificationType) ? pairIdOf(n) : null;
    navigate(pairId ? `/compatibility-control/view/${pairId}` : "/user-report");
  };

  // 🔴 SERVER SIDE. The dropdown's list is grouped and capped at ten, so it
  // can never name every unread notification. Asking the server to clear them
  // all is the only version of this that actually empties the bell.
  const handleMarkAllRead = async () => {
    try {
      const token = localStorage.getItem("authToken");
      await axios.post(
        `${API_BASE}/admin/notifications/mark-all-read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchNotifications();
    } catch (err) {
      console.error("Error marking all notifications as read:", err);
    }
  };

  return (
    <div className="dropdown">
      <button
        className="position-relative w-40 h-40 bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center"
        type="button"
        data-bs-toggle="dropdown"
      >
        <Icon icon="iconoir:bell" className="text-primary text-xl" />
        {unreadCount > 0 && (
          <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
            {unreadCount}
          </span>
        )}
      </button>

      <div className="dropdown-menu to-top dropdown-menu-lg p-0">
        <div className="m-16 py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex align-items-center justify-content-between gap-2">
          <h6 className="text-lg text-primary-light fw-semibold mb-0">
            Notifications
          </h6>
          <button
            className="text-sm text-primary-600"
            onClick={handleMarkAllRead}
          >
            Mark all read
          </button>
        </div>

        <div className="max-h-400-px overflow-y-auto scroll-sm pe-4">
          {notifications.length === 0 ? (
            <p className="px-24 py-12 text-sm text-secondary-light">
              No notifications found
            </p>
          ) : (
            // notifications.map((n) => (
            //   <Link
            //     to=""
            //     key={n._id}
            //     className={`px-24 py-12 d-flex align-items-start gap-3 mb-2 justify-content-between ${n.isRead === "0" ? "bg-light" : ""
            //       }`}
            //     onClick={() => handleClick(n)}
            //   >

            //     <div className="text-black hover-bg-transparent hover-text-primary d-flex align-items-center gap-3">

            //       <div className="d-flex flex-column">
            //         {notifications.length === 0 ? (
            //           <p className="px-24 py-12 text-sm text-secondary-light">
            //             No notifications found
            //           </p>
            //         ) : (
            //           notifications.map((n) => (
            //             <div key={n._id} className={`mb-2 ${n.isRead === "0" ? "bg-light" : ""}`}>
            //               <p className="mb-0 text-sm">
            //                 <strong>{n?.message}</strong>
            //               </p>
            //               <p className="text-xs text-secondary-light">{n.timeAgo}</p>

            //               {/* Optionally show count of grouped notifications */}
            //               {n.collepes && (
            //                 <p className="text-xs text-secondary-light">{n.collepes}</p>
            //               )}

            //             </div>
            //           ))
            //         )}


            //         {/* <p className="text-xs text-secondary-light">{n.timeAgo}</p> */}
            //       </div>

            //     </div>
            //   </Link>
            // ))

            notifications.map((n) => (
              <Link
                to=""
                key={n._id}
                className={`px-24 py-12 d-flex align-items-start gap-3 mb-2 justify-content-between ${n.isRead === "0" ? "bg-light" : ""
                  }`}
                onClick={() => handleClick(n)}
              >
                <div className="d-flex flex-column">
                  <p className="mb-0 text-sm">
                    <strong>{n?.message}</strong>
                  </p>
                  <p className="text-xs text-secondary-light">{n.timeAgo}</p>

                  {n.collepes && (
                    <p className="text-xs text-secondary-light">{n.collepes}</p>
                  )}
                </div>
              </Link>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminNotificationDropdown;
